import { useEffect, useState } from 'react';
import { getDataWithQueryString } from '../../../api/componentAPIs';
import { RMAXLogger } from '../../../services/LoggerService/RMAXLogger';
import { convertToNull } from './util';
import Events from '../../../utilities/EventEmitter';

const useForecastData = ({
  defaultValue,
  customerAPI,
  billsAPI,
  queryString,
  showErrorMessage,
  headerId,
  responseDataModifier
}) => {
  const [customers, setCustomers] = useState(defaultValue);
  const [bills, setBills] = useState(defaultValue);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [isBillsLoading, setIsBillsLoading] = useState(false);

  const handleFetchFailure = (type, setState, api, error) => {
    showErrorMessage(type);
    setState(convertToNull(defaultValue));
    RMAXLogger.error({ message: `API call failed ${api}`, error });
  };

  const resetRefreshBarTimer = () => {
    Events.emit(`ResetRefreshBarTimer-${headerId}`);
  };

  const fetchData = async (api, setState, type, abortController, setIsLoading) => {
    try {
      setIsLoading(true);
      const response = await getDataWithQueryString(api, queryString, {
        signal: abortController.signal
      });
      if (response.status === 200) {
        let data = response.data;
        if(responseDataModifier) {
          data = responseDataModifier(data, type);
        }
        setState(data);
        resetRefreshBarTimer();
      } else if(response.code !== "ERR_CANCELED") {
        handleFetchFailure(type, setState, api, response);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        handleFetchFailure(type, setState, api, error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllData = () => {
    const customerAbortController = new AbortController();
    const billsAbortController = new AbortController();

    setCustomers(defaultValue);
    setBills(defaultValue);

    fetchData(customerAPI, setCustomers, 'customer', customerAbortController, setIsCustomersLoading);
    fetchData(billsAPI, setBills, 'bills', billsAbortController, setIsBillsLoading);

    return () => {
      customerAbortController.abort();
      billsAbortController.abort();
    };
  };

   useEffect(() => {
    const abortFunction = fetchAllData();
    return () => {
      abortFunction?.();
    };
  }, [queryString]);

  return {
    customers,
    bills,
    isCustomersLoading,
    isBillsLoading,
    refreshData: fetchAllData
  };
};

export default useForecastData;
